import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import { api } from '@/lib/api';
import useCurrency from '@/Hooks/useCurrency';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches, useQueryParams } from '@/Pages/Operations/shared';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { DELIVERY_PERMISSIONS, DELIVERY_ROUTES, apiMessage, type DeliveryZone, type ServiceabilityResult } from '../deliveryApi';
import Money from './Money';
import PanelResult from './PanelResult';

/** §16 delivery zones and fees (ADR-0029 §2.3) — config surface, dispatch tiers only. */
export default function ZonesPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const canManage = can(DELIVERY_PERMISSIONS.deliveryZonesManage);

  const [branchId, setBranchId] = useState('');
  const [editing, setEditing] = useState<DeliveryZone | 'new' | null>(null);
  const [checking, setChecking] = useState(false);

  const params = useQueryParams({ branchId, limit: 200 });
  const zonesQuery = useApi<unknown>(DELIVERY_ROUTES.zones, params);
  const zones = rowsOf<DeliveryZone>(zonesQuery.data).rows;
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? id;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('delivery.zones.branch')}
          >
            <option value="">{t('delivery.zones.allBranches')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
        <Button size="sm" variant="outline" onClick={() => setChecking(true)}>
          <Icon as={Search} />
          {t('delivery.zones.checkServiceability')}
        </Button>
        {canManage && (
          <Button className="ms-auto" size="sm" onClick={() => setEditing('new')}>
            <Icon as={Plus} />
            {t('delivery.zones.add')}
          </Button>
        )}
      </div>

      <PanelResult
        loading={zonesQuery.loading && !zonesQuery.data}
        error={zonesQuery.error}
        count={zones.length}
        onRetry={zonesQuery.reload}
        subject={t('delivery.zones.subject')}
        filtered={Boolean(branchId)}
        onClearFilters={() => setBranchId('')}
        emptyTitle={t('delivery.zones.emptyTitle')}
        emptyHint={canManage ? t('delivery.zones.emptyHint') : undefined}
        emptyAction={canManage && <Button onClick={() => setEditing('new')}>{t('delivery.zones.add')}</Button>}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('delivery.zones.name')}</TH>
                <TH>{t('delivery.zones.branch')}</TH>
                <TH>{t('delivery.zones.coverage')}</TH>
                <TH className="text-end">{t('delivery.zones.baseFee')}</TH>
                <TH>{t('delivery.zones.active')}</TH>
                {canManage && <TH className="text-end">{t('delivery.zones.actions')}</TH>}
              </TR>
            </THead>
            <tbody>
              {zones.map((zone) => (
                <TR key={zone.id}>
                  <TD className="font-medium">{zone.name}</TD>
                  <TD>{branchName(zone.branchId)}</TD>
                  <TD className="text-xs text-ink-muted">
                    {zone.areas.length > 0 && (
                      <span>{t('delivery.zones.areasCount', { count: zone.areas.length })}</span>
                    )}
                    {zone.areas.length > 0 && zone.maxDistanceKm && ' · '}
                    {zone.maxDistanceKm && (
                      <span>{t('delivery.zones.withinKm', { km: zone.maxDistanceKm })}</span>
                    )}
                  </TD>
                  <TD className="text-end">
                    <Money amount={zone.baseFee} branchId={zone.branchId} />
                  </TD>
                  <TD>
                    <span
                      className={cn(
                        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
                        zone.active
                          ? 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300'
                          : 'bg-line-strong/30 text-ink-muted',
                      )}
                    >
                      {t(zone.active ? 'delivery.zones.activeYes' : 'delivery.zones.activeNo')}
                    </span>
                  </TD>
                  {canManage && (
                    <TD className="text-end">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEditing(zone)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              await api.delete(DELIVERY_ROUTES.zone(zone.id));
                              toast.success(t('delivery.zones.removed'));
                              zonesQuery.reload();
                            } catch (e) {
                              toast.error(apiMessage(e, t('delivery.zones.actionFailed')));
                            }
                          }}
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                    </TD>
                  )}
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {editing && (
        <ZoneFormDialog
          zone={editing === 'new' ? null : editing}
          branches={branches}
          onClose={() => setEditing(null)}
          onDone={zonesQuery.reload}
        />
      )}
      {checking && <ServiceabilityDialog branches={branches} onClose={() => setChecking(false)} />}
    </div>
  );
}

function ZoneFormDialog({
  zone,
  branches,
  onClose,
  onDone,
}: {
  zone: DeliveryZone | null;
  branches: { id: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [branchId, setBranchId] = useState(zone?.branchId ?? '');
  const { code } = useCurrency(branchId || null);
  const [name, setName] = useState(zone?.name ?? '');
  const [active, setActive] = useState(zone?.active ?? true);
  const [priority, setPriority] = useState(String(zone?.priority ?? 100));
  const [areasText, setAreasText] = useState((zone?.areas ?? []).join(', '));
  const [maxDistanceKm, setMaxDistanceKm] = useState(zone?.maxDistanceKm ?? '');
  const [baseFee, setBaseFee] = useState(zone?.baseFee ?? '0');
  const [perKmFee, setPerKmFee] = useState(zone?.perKmFee ?? '');
  const [minOrderAmount, setMinOrderAmount] = useState(zone?.minOrderAmount ?? '');
  const [freeDeliveryAboveAmount, setFreeDeliveryAboveAmount] = useState(
    zone?.freeDeliveryAboveAmount ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  const areas = areasText
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
  // Mirrors the backend's own guard (`assertHasPredicate`): an empty zone
  // would silently match every address, so the form refuses to submit one
  // rather than letting the server 400 explain it after the fact.
  const missingPredicate = areas.length === 0 && !maxDistanceKm;

  const submit = async () => {
    if (!name.trim() || !branchId || missingPredicate) return;
    setSubmitting(true);
    const body = {
      name: name.trim(),
      active,
      priority: Number(priority) || 100,
      areas,
      maxDistanceKm: maxDistanceKm ? Number(maxDistanceKm) : undefined,
      baseFee,
      perKmFee: perKmFee.trim() || undefined,
      minOrderAmount: minOrderAmount.trim() || undefined,
      freeDeliveryAboveAmount: freeDeliveryAboveAmount.trim() || undefined,
    };
    try {
      if (zone) {
        await api.patch(DELIVERY_ROUTES.zone(zone.id), body);
      } else {
        await api.post(DELIVERY_ROUTES.zones, { ...body, branchId });
      }
      toast.success(t(zone ? 'delivery.zones.updated' : 'delivery.zones.created'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.zones.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(zone ? 'delivery.zones.editTitle' : 'delivery.zones.addTitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || !name.trim() || !branchId || missingPredicate}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('delivery.zones.branch')} required>
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={Boolean(zone)}>
            <option value="">{t('delivery.zones.branchPlaceholder')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('delivery.zones.name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label={t('delivery.zones.areas')}
          hint={t('delivery.zones.areasHint')}
          error={missingPredicate ? t('delivery.zones.predicateRequired') : undefined}
        >
          <Input value={areasText} onChange={(e) => setAreasText(e.target.value)} />
        </Field>
        <Field label={t('delivery.zones.maxDistanceKm')} hint={t('delivery.zones.maxDistanceKmHint')}>
          <Input
            type="number"
            inputMode="decimal"
            min="0.1"
            max="500"
            value={maxDistanceKm}
            onChange={(e) => setMaxDistanceKm(e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('delivery.zones.baseFee', { currency: code })} required>
            <Input type="number" inputMode="decimal" min="0" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} />
          </Field>
          <Field label={t('delivery.zones.perKmFee', { currency: code })} hint={t('delivery.zones.perKmFeeHint')}>
            <Input type="number" inputMode="decimal" min="0" value={perKmFee} onChange={(e) => setPerKmFee(e.target.value)} />
          </Field>
          <Field label={t('delivery.zones.minOrderAmount', { currency: code })}>
            <Input type="number" inputMode="decimal" min="0" value={minOrderAmount} onChange={(e) => setMinOrderAmount(e.target.value)} />
          </Field>
          <Field label={t('delivery.zones.freeDeliveryAboveAmount', { currency: code })}>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              value={freeDeliveryAboveAmount}
              onChange={(e) => setFreeDeliveryAboveAmount(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t('delivery.zones.priority')} hint={t('delivery.zones.priorityHint')}>
          <Input type="number" min="0" value={priority} onChange={(e) => setPriority(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          {t('delivery.zones.active')}
        </label>
      </div>
    </Dialog>
  );
}

function ServiceabilityDialog({
  branches,
  onClose,
}: {
  branches: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [branchId, setBranchId] = useState('');
  const [addressId, setAddressId] = useState('');
  const [orderSubtotal, setOrderSubtotal] = useState('');
  const [result, setResult] = useState<ServiceabilityResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const run = async () => {
    if (!branchId || !addressId.trim()) return;
    setChecking(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.get<ServiceabilityResult>(DELIVERY_ROUTES.zoneServiceability, {
        params: { branchId, addressId: addressId.trim(), orderSubtotal: orderSubtotal.trim() || undefined },
      });
      setResult(data);
    } catch (e) {
      setError(apiMessage(e, t('delivery.zones.checkFailed')));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('delivery.zones.checkServiceability')}
      description={t('delivery.zones.checkServiceabilityHint')}
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label={t('delivery.zones.branch')} required>
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">{t('delivery.zones.branchPlaceholder')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('delivery.zones.addressId')} required hint={t('delivery.zones.addressIdHint')}>
          <Input value={addressId} onChange={(e) => setAddressId(e.target.value)} />
        </Field>
        <Field label={t('delivery.zones.orderSubtotal')}>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            value={orderSubtotal}
            onChange={(e) => setOrderSubtotal(e.target.value)}
          />
        </Field>
        <Button onClick={run} disabled={checking || !branchId || !addressId.trim()}>
          {t('delivery.zones.check')}
        </Button>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {result && (
          <dl className="space-y-2 rounded-lg border p-4 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-ink-muted">{t('delivery.zones.serviceable')}</dt>
              <dd>{t(result.serviceable ? 'common.yes' : 'common.no')}</dd>
            </div>
            {result.zoneName && (
              <div className="flex items-center justify-between">
                <dt className="text-ink-muted">{t('delivery.zones.matchedZone')}</dt>
                <dd>{result.zoneName}</dd>
              </div>
            )}
            {result.distanceKm !== null && (
              <div className="flex items-center justify-between">
                <dt className="text-ink-muted">{t('delivery.zones.distance')}</dt>
                <dd className="tabular-nums">{result.distanceKm} km</dd>
              </div>
            )}
            <div className="flex items-center justify-between">
              <dt className="text-ink-muted">{t('delivery.zones.fee')}</dt>
              <dd className="tabular-nums">
                <Money amount={result.feeAmount} branchId={branchId} />
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-muted">{t('delivery.zones.meetsMinimumOrder')}</dt>
              <dd>{t(result.meetsMinimumOrder ? 'common.yes' : 'common.no')}</dd>
            </div>
          </dl>
        )}
      </div>
    </Dialog>
  );
}
